import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MovieItem } from '@/mocks/movies';

interface HeroCarouselProps {
  movies: MovieItem[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const navigate = useNavigate();

  const goTo = useCallback((index: number) => {
    setActive((index + movies.length) % movies.length);
  }, [movies.length]);

  useEffect(() => {
    const timer = setInterval(() => goTo(active + 1), 6000);
    return () => clearInterval(timer);
  }, [active, goTo]);

  if (movies.length === 0) return null;
  const current = movies[active];

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden bg-primary-950">
      {movies.map((movie, index) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${index === active ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
        >
          <img
            src={movie.banner}
            alt={`${movie.title} 海报大图`}
            title={movie.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/50 to-primary-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/80 via-primary-950/20 to-transparent"></div>
        </div>
      ))}

      <div className="relative z-20 h-full max-w-[1280px] mx-auto px-4 md:px-6 flex items-end md:items-center pb-10 md:pb-0">
        <div className="w-full max-w-xl animate-fade-up" key={current.id}>
          <span className="inline-flex items-center gap-1.5 bg-accent-500 text-background-50 text-xs font-medium px-3 py-1 rounded-full">
            <i className="ri-fire-fill"></i> 热门推荐
          </span>
          <h1 className="mt-4 text-3xl md:text-5xl font-bold text-background-50 leading-tight">
            {current.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-background-200">
            <span className="flex items-center gap-1"><i className="ri-star-fill text-accent-400"></i>{current.rating.toFixed(1)}</span>
            <span>{current.year}</span>
            <span>{current.region}</span>
            <span className="flex flex-wrap gap-1.5">
              {current.genres.map((g) => (
                <span key={g} className="px-2 py-0.5 bg-background-50/10 rounded-full">{g}</span>
              ))}
            </span>
          </div>
          <p className="mt-4 text-sm md:text-base text-background-300 line-clamp-2 md:line-clamp-3">
            {current.description}
          </p>
          <button
            onClick={() => navigate(`/detail/${current.id}`)}
            className="mt-6 inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 dark:text-foreground-950 px-6 py-3 rounded-full font-medium text-sm whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-play-fill text-lg"></i> 查看详情
          </button>
        </div>
      </div>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {movies.map((movie, index) => (
          <button
            key={movie.id}
            onClick={() => goTo(index)}
            aria-label={`切换到 ${movie.title}`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${index === active ? 'w-8 bg-accent-500' : 'w-4 bg-background-50/40'}`}
          ></button>
        ))}
      </div>

      <button
        onClick={() => goTo(active - 1)}
        aria-label="上一张"
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={() => goTo(active + 1)}
        aria-label="下一张"
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>
    </section>
  );
}